import React, { useEffect, useState } from "react";
import { icons } from "../../../Assets/Icons/icons";
import { useClickOutside } from "../../Hooks/useClickOutside";
import { uploadToCloudinary } from "../../../utils/cloudinary";
import { Loader2 } from "lucide-react";
import axios from "axios";
import Swal from "sweetalert2";
import toast from "react-hot-toast";

const AddNewModal = ({ onClose, isAddModal, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: {
      en: "",
      hi: "",
      es: "",
      zh: ""
    },
    description: {
      en: "",
      hi: "",
      es: "",
      zh: ""
    },
    imgUrl: "",
    catIconUrl: "",
    sequence: 0,
  });

  const [imageFile, setImageFile] = useState(null);
  const [iconFile, setIconFile] = useState(null);
  const [animate, setAnimate] = useState(false);
  const domNode = useClickOutside(() => handleClose());
  const [loading, setLoading] = useState(false);

  const handleChange = (e, field, lang = null) => {
    const { value } = e.target;
    
    if (field === "name" || field === "description") {
      setFormData(prev => ({
        ...prev,
        [field]: {
          ...prev[field],
          [lang]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name.en.trim()) {
      toast.error("English name is required");
      return;
    }
    
    if (!formData.description.en.trim()) {
      toast.error("English description is required");
      return;
    }

    try {
      setLoading(true);
      let imgUrl = formData.imgUrl;
      let catIconUrl = formData.catIconUrl;

      // Upload images
      if (imageFile) {
        const uploadedImg = await uploadToCloudinary(imageFile);
        imgUrl = uploadedImg?.secure_url || uploadedImg?.url;
      }

      if (iconFile) {
        const uploadedIcon = await uploadToCloudinary(iconFile);
        catIconUrl = uploadedIcon?.secure_url || uploadedIcon?.url;
      }

      const finalData = {
        ...formData,
        imgUrl,
        catIconUrl,
        name: {
          en: formData.name.en.trim(),
          hi: formData.name.hi.trim() || formData.name.en.trim(),
          es: formData.name.es.trim() || formData.name.en.trim(),
          zh: formData.name.zh.trim() || formData.name.en.trim()
        },
        description: {
          en: formData.description.en.trim(),
          hi: formData.description.hi.trim() || formData.description.en.trim(),
          es: formData.description.es.trim() || formData.description.en.trim(),
          zh: formData.description.zh.trim() || formData.description.en.trim()
        }
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/categories/create-category`,
        finalData
      );

      if (response.status >= 200 && response.status < 205) {
        Swal.fire("Created", response.data.message, "success");
        onSuccess();
        onClose();
      }
    } catch (error) {
      console.error("Category creation error:", error);
      toast.error(error?.response?.data?.message || "Failed to create category");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAddModal) {
      setTimeout(() => setAnimate(true), 10);
    }
  }, [isAddModal]);

  const handleClose = () => {
    setAnimate(false);
    setTimeout(() => {
      onClose();
    }, 250);
  };

  return (
    <section
      className={`h-screen w-full ${
        animate ? "opacity-100" : "opacity-0"
      } transition-opacity duration-200 z-50 flex justify-center items-center fixed inset-0 bg-black/50 backdrop-blur-sm`}
    >
      <div
        ref={domNode}
        className={`bg-[#ecf1f5] relative w-[95%] sm:w-[90%] md:w-[85%] lg:w-[80%] xl:w-[75%]
        max-h-[90vh] overflow-y-auto p-4 sm:p-6 rounded-xl transform transition-all duration-300
        ${animate ? "scale-100 opacity-100 translate-y-0" : "scale-95 opacity-0 translate-y-5"}`}
      >
        {/* Close Button */}
        <div className="flex justify-end absolute top-3 right-3 items-center">
          <span
            onClick={handleClose}
            className="p-3 rounded-full bg-white hover:text-[#2275fc] transition-colors duration-300 cursor-pointer"
          >
            {icons.closeIcon}
          </span>
        </div>

        {/* Title */}
        <h1 className="text-[1.5rem] font-philosopher mb-2 sm:text-[2rem] text-primaryTextColor capitalize">
          Create New Category (Multilingual)
        </h1>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          method="post"
          className="flex flex-col space-y-4 sm:space-y-6"
        >
          {/* Sequence */}
          <div className="w-full md:w-1/2">
            <div className="flex flex-col space-y-1">
              <label className="font-medium text-gray-700">
                Sequence Number <span className="text-[#ff5200]">*</span>
              </label>
              <input
                required
                onChange={(e) => handleChange(e, "sequence")}
                value={formData.sequence}
                className="outline-none focus:ring-2 ring-[#2275fc] px-3 py-2.5 rounded-lg border border-gray-300 w-full"
                type="number"
                min="0"
                placeholder="0"
              />
              <p className="text-xs text-gray-500 mt-1">
                Determines display order (lower numbers appear first)
              </p>
            </div>
          </div>

          {/* Category Names - All Languages */}
          <div className="space-y-3">
            <label className="font-medium text-gray-700">
              Category Name <span className="text-[#ff5200]">*</span>
              <span className="text-xs text-gray-500 ml-2">(Fill in all languages)</span>
            </label>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* English Name */}
              <div className="relative">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-700">🇺🇸 English *</span>
                  {!formData.name.en.trim() && (
                    <span className="text-xs text-red-500">Required</span>
                  )}
                </div>
                <input
                  required
                  onChange={(e) => handleChange(e, "name", "en")}
                  value={formData.name.en}
                  className="outline-none focus:ring-2 ring-[#2275fc] px-3 py-2.5 rounded-lg border border-blue-300 bg-blue-50 w-full"
                  type="text"
                  placeholder="English name"
                />
              </div>

              {/* Hindi Name */}
              <div className="relative">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-700">🇮🇳 Hindi</span>
                </div>
                <input
                  onChange={(e) => handleChange(e, "name", "hi")}
                  value={formData.name.hi}
                  className="outline-none focus:ring-2 ring-[#2275fc] px-3 py-2.5 rounded-lg border border-gray-300 w-full"
                  type="text"
                  placeholder="Hindi name"
                />
              </div>

              {/* Spanish Name */}
              <div className="relative">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-700">🇪🇸 Spanish</span>
                </div>
                <input
                  onChange={(e) => handleChange(e, "name", "es")}
                  value={formData.name.es}
                  className="outline-none focus:ring-2 ring-[#2275fc] px-3 py-2.5 rounded-lg border border-gray-300 w-full"
                  type="text"
                  placeholder="Spanish name"
                />
              </div>

              {/* Chinese Name */}
              <div className="relative">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-700">🇨🇳 Chinese</span>
                </div>
                <input
                  onChange={(e) => handleChange(e, "name", "zh")}
                  value={formData.name.zh}
                  className="outline-none focus:ring-2 ring-[#2275fc] px-3 py-2.5 rounded-lg border border-gray-300 w-full"
                  type="text"
                  placeholder="Chinese name"
                />
              </div>
            </div>
            
            <p className="text-xs text-gray-500">
              English name is required. Other languages will fallback to English if left empty.
            </p>
          </div>

          {/* Category Descriptions - All Languages */}
          <div className="space-y-3">
            <label className="font-medium text-gray-700">
              Description <span className="text-[#ff5200]">*</span>
              <span className="text-xs text-gray-500 ml-2">(Fill in all languages)</span>
            </label>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* English Description */}
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">🇺🇸 English *</span>
                  {!formData.description.en.trim() && (
                    <span className="text-xs text-red-500">Required</span>
                  )}
                </div>
                <textarea
                  required
                  onChange={(e) => handleChange(e, "description", "en")}
                  value={formData.description.en}
                  className="outline-none focus:ring-2 ring-[#2275fc] px-3 py-2.5 rounded-lg border border-blue-300 bg-blue-50 w-full h-32"
                  placeholder="English description"
                />
              </div>

              {/* Hindi Description */}
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">🇮🇳 Hindi</span>
                </div>
                <textarea
                  onChange={(e) => handleChange(e, "description", "hi")}
                  value={formData.description.hi}
                  className="outline-none focus:ring-2 ring-[#2275fc] px-3 py-2.5 rounded-lg border border-gray-300 w-full h-32"
                  placeholder="Hindi description"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Spanish Description */}
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">🇪🇸 Spanish</span>
                </div>
                <textarea
                  onChange={(e) => handleChange(e, "description", "es")}
                  value={formData.description.es}
                  className="outline-none focus:ring-2 ring-[#2275fc] px-3 py-2.5 rounded-lg border border-gray-300 w-full h-32"
                  placeholder="Spanish description"
                />
              </div>

              {/* Chinese Description */}
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">🇨🇳 Chinese</span>
                </div>
                <textarea
                  onChange={(e) => handleChange(e, "description", "zh")}
                  value={formData.description.zh}
                  className="outline-none focus:ring-2 ring-[#2275fc] px-3 py-2.5 rounded-lg border border-gray-300 w-full h-32"
                  placeholder="Chinese description"
                />
              </div>
            </div>
            
            <p className="text-xs text-gray-500">
              English description is required. Other languages will fallback to English if left empty.
            </p>
          </div>

          {/* Image & Icon Upload */}
          <div className="grid xl:grid-cols-2 grid-cols-1 gap-6">
            {/* Upload Image */}
            <div className="flex flex-col space-y-1">
              <label className="font-medium text-gray-700">Category Image</label>
              <label
                htmlFor="imgUrl"
                className="border-2 border-dashed border-blue-400 overflow-hidden relative flex-1 py-10 px-5 rounded-xl flex items-center justify-center flex-col cursor-pointer bg-blue-50/50 hover:bg-blue-50 transition-colors text-center"
              >
                <span className="text-blue-500 text-4xl z-10">
                  {icons.uploadIcon}
                </span>
                <p className="z-10 text-sm mt-2">
                  Drop your image here or{" "}
                  <span className="text-blue-600 font-medium">click to browse</span>
                </p>
                <p className="z-10 text-xs text-gray-500 mt-1">
                  Recommended: 800x600px or larger
                </p>
                {(imageFile || formData.imgUrl) && (
                  <img
                    src={
                      imageFile
                        ? URL.createObjectURL(imageFile)
                        : formData.imgUrl
                    }
                    alt="Preview"
                    className="h-full w-full object-cover absolute top-0 opacity-30"
                  />
                )}
              </label>
              <input
                onChange={(e) => setImageFile(e.target.files[0])}
                type="file"
                accept="image/*"
                id="imgUrl"
                className="hidden"
              />
            </div>

            {/* Upload Icon */}
            <div className="flex flex-col space-y-1">
              <label className="font-medium text-gray-700">Category Icon</label>
              <label
                htmlFor="catIconUrl"
                className="border-2 border-dashed border-green-400 overflow-hidden relative flex-1 py-10 px-5 rounded-xl flex items-center justify-center border-2 flex-col cursor-pointer bg-green-50/50 hover:bg-green-50 transition-colors text-center"
              >
                <span className="text-green-500 text-4xl z-10">
                  {icons.uploadIcon}
                </span>
                <p className="z-10 text-sm mt-2">
                  Drop your icon here or{" "}
                  <span className="text-green-600 font-medium">click to browse</span>
                </p>
                <p className="z-10 text-xs text-gray-500 mt-1">
                  Recommended: 128x128px SVG or PNG
                </p>
                {(iconFile || formData.catIconUrl) && (
                  <img
                    src={
                      iconFile
                        ? URL.createObjectURL(iconFile)
                        : formData.catIconUrl
                    }
                    alt="Preview"
                    className="h-full w-full object-cover absolute top-0 opacity-30"
                  />
                )}
              </label>
              <input
                onChange={(e) => setIconFile(e.target.files[0])}
                type="file"
                accept="image/*"
                id="catIconUrl"
                className="hidden"
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end items-center pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-2.5 mr-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors duration-300 font-medium"
            >
              Cancel
            </button>
            <button
              disabled={loading}
              type="submit"
              className={`text-white flex items-center justify-center bg-[#b07d17] rounded-lg hover:bg-[#8c6312] transition-colors duration-300 px-8 font-bold py-2.5 text-sm sm:text-base min-w-[120px] ${
                loading ? "opacity-70 cursor-not-allowed" : ""
              }`}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create Category"
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default AddNewModal;